<template>
  <div class="nhso-dash">
    <header class="nd-header">
      <img class="nd-logo" :src="logo" alt="DentCloud" />
      <div class="nd-titles">
        <h1>DentCloud × คลินิกทันตกรรมอบอุ่น</h1>
        <p class="nd-sub">
          ภาพรวมบริการทันตกรรมสิทธิ สปสช. "30 บาทรักษาทุกที่" —
          ข้อมูลรวมยอดแบบนิรนาม ไม่มีข้อมูลระบุตัวบุคคล
        </p>
      </div>
      <span v-if="payload && payload.dataAsOf" class="nd-asof">
        ข้อมูล ณ วันที่ {{ asOfText }}
      </span>
    </header>

    <NhsoFilterBar
      :value="filters"
      :loading="loading"
      @change="onFilterChange"
      @export="onExport"
    />

    <div v-if="loading && !payload" class="nd-loading">
      <b-spinner />
    </div>

    <div v-if="errored && !payload" class="nd-error">
      ไม่สามารถโหลดข้อมูลได้ในขณะนี้ กรุณาลองใหม่อีกครั้ง
    </div>

    <div v-if="payload" :class="{ 'nd-refetching': loading }">
      <!-- KPI row -->
      <div class="nd-kpis">
        <KpiCard label="ครั้งที่รับบริการ" :value="payload.kpi.visits" />
        <KpiCard label="ผู้รับบริการ (ไม่ซ้ำ)" :value="payload.kpi.patients" />
        <KpiCard label="รายการหัตถการ" :value="payload.kpi.items" :context="avgItemsText" />
        <KpiCard
          label="ทันตแพทย์ผู้ให้บริการ"
          :value="payload.kpi.dentists"
          context="ไม่ซ้ำ ในช่วงที่เลือก"
        />
        <KpiCard label="สาขาที่ให้บริการ" :value="payload.kpi.branches" />
        <KpiCard :label="coverageLabel" :value="coverageValue" :context="coverageContext" />
      </div>

      <!-- Area ranking + monthly trend -->
      <div class="nd-grid2">
        <SectionCard :title="areaTitle" hint="ครั้งที่รับบริการ · สลับมุมมองได้ที่ตัวกรอง &quot;พื้นที่&quot; ด้านบน">
          <RankedBarList :items="areaItems" :top-n="8" unit="ครั้ง" name-header="พื้นที่" />
        </SectionCard>
        <SectionCard title="แนวโน้มรายเดือน แยกตามสิทธิ" hint="จำนวนครั้งที่รับบริการรายเดือน · ชี้ที่กราฟเพื่อดูค่า">
          <LineChart
            :chart-data="trendChartData"
            :options="trendOptions"
            :styles="{ position: 'relative', height: '260px' }"
          />
          <details class="nd-details">
            <summary>ดูข้อมูลเป็นตาราง</summary>
            <table class="nd-table">
              <tr>
                <th>เดือน</th>
                <th v-for="s in trendSeries" :key="s">{{ s }}</th>
              </tr>
              <tr v-for="m in trendMonths" :key="m">
                <td>{{ m }}</td>
                <td v-for="s in trendSeries" :key="s">{{ trendCell(m, s) }}</td>
              </tr>
            </table>
          </details>
        </SectionCard>
      </div>

      <!-- Timeslot heatmap + off-hours -->
      <div class="nd-grid2">
        <SectionCard
          title="ช่วงเวลาที่รับบริการ"
          hint="สัดส่วนครั้งที่รับบริการ แยกตามวันและช่วงเวลา"
        >
          <HeatmapTable :cols="slotCols" :rows="timeslotRows" :ramp="blueRamp" suffix="%" />
        </SectionCard>
        <SectionCard
          title="การเข้าถึงนอกเวลาราชการ"
          hint="จุดแข็งของคลินิกเอกชนในระบบ — ให้บริการช่วงที่หน่วยบริการรัฐส่วนใหญ่ปิด"
        >
          <div class="nd-kpis nd-kpis-2">
            <KpiCard
              label="รับบริการนอกเวลาราชการ<br>(หลัง 17:00 น. วันธรรมดา)"
              :value="offHoursPct + '%'"
              :context="fmt(offHoursVisits) + ' ครั้ง'"
            />
            <KpiCard
              label="รับบริการวันเสาร์–อาทิตย์"
              :value="weekendPct + '%'"
              :context="fmt(weekendVisits) + ' ครั้ง'"
            />
          </div>
        </SectionCard>
      </div>

      <!-- Treatment Top 20 + ICD-10 Top 20 -->
      <div class="nd-grid2">
        <SectionCard
          title="ประเภทการรักษา (Top 20)"
          :hint="`จัดอันดับจากรายการหัตถการ NHSO ${fmt(payload.kpi.items)} รายการ`"
        >
          <RankedBarList
            :items="treatItems"
            :top-n="5"
            unit="รายการ"
            name-header="ประเภทการรักษา"
          />
        </SectionCard>
        <SectionCard
          title="กลุ่มโรคที่พบมากที่สุด (ICD-10, Top 20)"
          hint="จากการวินิจฉัยที่บันทึกคู่กับหัตถการ"
        >
          <RankedBarList
            :items="diagItems"
            :top-n="5"
            unit="ครั้งที่วินิจฉัย"
            name-header="กลุ่มโรค"
          />
        </SectionCard>
      </div>

      <!-- Treatment x age + Diagnosis x age -->
      <div class="nd-grid2">
        <SectionCard
          title="กลุ่มการรักษาตามช่วงอายุ"
          hint="สัดส่วนภายในแต่ละประเภทการรักษา (แถวละ 100%)"
        >
          <HeatmapTable :cols="ageBands" :rows="treatAgeRows" :ramp="blueRamp" suffix="%" :top-n="5" />
        </SectionCard>
        <SectionCard
          title="กลุ่มโรคตามช่วงอายุ"
          hint="สัดส่วนภายในแต่ละกลุ่มโรค (แถวละ 100%)"
        >
          <HeatmapTable :cols="ageBands" :rows="diagAgeRows" :ramp="aquaRamp" suffix="%" :top-n="5" />
        </SectionCard>
      </div>

      <!-- Age bars/gender + funnel -->
      <div class="nd-grid2">
        <SectionCard
          title="ผู้รับบริการตามช่วงอายุ"
          hint="สัดส่วนของผู้รับบริการ (เรียงตามช่วงอายุ — สีเข้มขึ้นตามอายุ)"
        >
          <RankedBarList
            :items="ageItems"
            :colors="ordinalRamp"
            unit="คน"
            name-header="ช่วงอายุ"
          />
          <div class="sec-sub">เพศ</div>
          <div class="nd-gender">
            <div
              v-for="g in genderSegments"
              :key="g.name"
              class="nd-gender-seg"
              :style="{ flexBasis: g.pct + '%', background: g.color }"
              :title="`${g.name} ${g.pct}%`"
            >
              {{ g.name }} {{ g.pct }}%
            </div>
          </div>
        </SectionCard>
        <SectionCard
          title="การกลับมาใช้สิทธิ ครั้งที่ 1 → 2 → 3"
          hint="สิทธิทันตกรรมใช้ได้สูงสุด 3 ครั้ง/ปี — สัดส่วนผู้ที่กลับมารักษาต่อเนื่อง"
        >
          <RankedBarList
            :items="funnelItems"
            :colors="ordinalRamp"
            unit="ครั้ง"
            name-header="ครั้งที่"
          />
        </SectionCard>
      </div>

      <!-- New vs returning (full width) -->
      <SectionCard
        title="ผู้ใช้สิทธิรายใหม่ vs รายเดิม"
        hint="ผู้รับบริการต่อเดือน แยกผู้ใช้สิทธิครั้งแรกกับผู้ที่เคยใช้แล้ว"
      >
        <BarChart
          :chart-data="newRetChartData"
          :options="stackedBarOptions"
          :styles="{ position: 'relative', height: '240px' }"
        />
      </SectionCard>
    </div>

    <footer class="nd-credit">
      จัดทำโดย <b>DentCloud</b> ร่วมกับ <b>คลินิกทันตกรรมอบอุ่น</b> ·
      ข้อมูลรวบรวมจากนัดหมายสิทธิ สปสช. ของคลินิกเครือข่าย แสดงแบบนิรนาม ·
      กลุ่มข้อมูลน้อยกว่า 5 ราย แสดงเป็น "&lt;5" เพื่อคุ้มครองความเป็นส่วนตัว
    </footer>
  </div>
</template>

<script>
import KpiCard from "@/components/nhsoPublicDashboard/KpiCard.vue";
import SectionCard from "@/components/nhsoPublicDashboard/SectionCard.vue";
import RankedBarList from "@/components/nhsoPublicDashboard/RankedBarList.vue";
import HeatmapTable from "@/components/nhsoPublicDashboard/HeatmapTable.vue";
import NhsoFilterBar from "@/components/nhsoPublicDashboard/NhsoFilterBar.vue";
import { numVal, fmtVal, pctOf } from "@/utils/nhsoDashboardFormat";
import { exportDashboardExcel } from "@/utils/nhsoDashboardExport";
import dentcloudLogo from "@/assets/img/dentcloud-logo-darkblue.png";

const AGE_BANDS = ["0-14", "15-24", "25-44", "45-59", "60+"];
const TREND_COLORS = { D10: "#2a78d6", D11: "#1baf7a", D20: "#eda100", UNK: "#7c8b99" };

export default {
  name: "NhsoPublicDashboard",
  components: {
    KpiCard,
    SectionCard,
    RankedBarList,
    HeatmapTable,
    NhsoFilterBar,
    LineChart: () => import("@/components/chart/LineChart.vue"),
    BarChart: () => import("@/components/chart/BarChart.vue"),
  },
  data() {
    return {
      logo: dentcloudLogo,
      loading: false,
      errored: false,
      payload: null,
      filters: { period: "ALL", areaType: "all", area: "ALL", subitem: "ALL" },
    };
  },
  computed: {
    asOfText() {
      return new Date(this.payload.dataAsOf).toLocaleDateString("th-TH", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    },
    avgItemsText() {
      const v = numVal(this.payload.kpi.visits);
      const i = numVal(this.payload.kpi.items);
      return v > 0 ? `เฉลี่ย ${(i / v).toFixed(1)} รายการ/ครั้ง` : "";
    },
    coverageLabel() {
      return this.filters.areaType === "province" ? "พื้นที่ที่เลือก" : "พื้นที่ครอบคลุม";
    },
    coverageValue() {
      if (this.filters.areaType === "province") {
        const tokens = String(this.filters.area).split(",").filter(Boolean);
        return tokens.length === 1 ? tokens[0] : `${tokens.length} จังหวัด`;
      }
      return `${fmtVal(this.payload.kpi.provinces)} จว.`;
    },
    coverageContext() {
      if (this.filters.areaType === "all") return "ครอบคลุมทั่วประเทศ";
      if (this.filters.areaType === "zone") return "จังหวัดที่มีบริการในเขตนี้";
      if (this.filters.areaType === "region") return "จังหวัดที่มีบริการในภาคนี้";
      return "";
    },
    areaTitle() {
      const t = this.filters.areaType;
      if (t === "all") return "การรับบริการตามเขตสุขภาพ";
      if (t === "zone") return "จังหวัดในเขตสุขภาพที่เลือก";
      if (t === "region") return "จังหวัดในภาคที่เลือก";
      return "จังหวัดที่เลือก เทียบภาพรวม";
    },
    areaItems() {
      return this.payload.areaRanking.map(r => ({ name: r.name, value: r.visits }));
    },
    trendMonths() {
      return [...new Set(this.payload.trend.map(r => r.month))].sort();
    },
    trendSeries() {
      const present = new Set(this.payload.trend.map(r => r.subitem));
      return ["D10", "D11", "D20", "UNK"].filter(s => present.has(s));
    },
    trendChartData() {
      return {
        labels: this.trendMonths,
        datasets: this.trendSeries.map(s => ({
          label: s,
          borderColor: TREND_COLORS[s] || "#2a78d6",
          backgroundColor: "transparent",
          borderWidth: 2.5,
          pointRadius: 2,
          pointBackgroundColor: TREND_COLORS[s] || "#2a78d6",
          lineTension: 0.25,
          data: this.trendMonths.map(m => numVal(this.trendCellRaw(m, s))),
        })),
      };
    },
    trendOptions() {
      return {
        maintainAspectRatio: false,
        legend: {
          position: "top",
          align: "start",
          labels: { boxWidth: 14, fontFamily: "IBM Plex Sans Thai", fontColor: "#46586a" },
        },
        scales: {
          yAxes: [
            {
              scaleLabel: {
                display: true,
                labelString: "จำนวนครั้งที่รับบริการ",
                fontFamily: "IBM Plex Sans Thai",
                fontColor: "#7c8b99",
              },
              ticks: { beginAtZero: true, callback: v => v.toLocaleString("th-TH") },
              gridLines: { color: "#e3e9ef" },
            },
          ],
          xAxes: [{ gridLines: { display: false } }],
        },
        tooltips: { mode: "index", intersect: false },
      };
    },
    slotCols() {
      return ["8–10 น.", "10–12 น.", "12–14 น.", "14–17 น.", "17–20 น."];
    },
    timeslotRows() {
      const buckets = [
        [8, 10],
        [10, 12],
        [12, 14],
        [14, 17],
        [17, 20],
      ];
      const groups = [
        { name: "จันทร์–ศุกร์", dows: [0, 1, 2, 3, 4] },
        { name: "เสาร์", dows: [5] },
        { name: "อาทิตย์", dows: [6] },
      ];
      const total = this.payload.timeslot.reduce((s, r) => s + numVal(r.visits), 0) || 1;
      return groups.map(g => ({
        name: g.name,
        values: buckets.map(([a, b]) =>
          Math.round(
            (this.payload.timeslot
              .filter(r => g.dows.includes(r.dow) && r.hour >= a && r.hour < b)
              .reduce((s, r) => s + numVal(r.visits), 0) /
              total) *
              100
          )
        ),
      }));
    },
    offHoursVisits() {
      return this.payload.timeslot
        .filter(r => r.dow <= 4 && r.hour >= 17)
        .reduce((s, r) => s + numVal(r.visits), 0);
    },
    weekendVisits() {
      return this.payload.timeslot
        .filter(r => r.dow >= 5)
        .reduce((s, r) => s + numVal(r.visits), 0);
    },
    offHoursPct() {
      return pctOf(this.offHoursVisits, this.payload.kpi.visits);
    },
    weekendPct() {
      return pctOf(this.weekendVisits, this.payload.kpi.visits);
    },
    treatItems() {
      return this.payload.treatTop.map(r => ({ name: r.name, value: r.count }));
    },
    diagItems() {
      return this.payload.diagTop.map(r => ({ name: this.diagLabel(r.code), value: r.count }));
    },
    ageBands() {
      return AGE_BANDS;
    },
    diagAgeRows() {
      const codes = [...new Set(this.payload.diagAge.map(r => r.code))].slice(0, 20);
      return codes.map(code => {
        const rowTotal =
          this.payload.diagAge
            .filter(r => r.code === code)
            .reduce((s, r) => s + numVal(r.count), 0) || 1;
        return {
          name: this.diagLabel(code),
          values: AGE_BANDS.map(b => {
            const found = this.payload.diagAge.find(r => r.code === code && r.ageBand === b);
            return Math.round((numVal(found && found.count) / rowTotal) * 100);
          }),
        };
      });
    },
    treatAgeRows() {
      const rows = this.payload.treatAge || [];
      const totals = {};
      for (const r of rows) totals[r.name] = (totals[r.name] || 0) + numVal(r.count);
      const names = Object.keys(totals)
        .sort((a, b) => totals[b] - totals[a])
        .slice(0, 20);
      return names.map(name => {
        const rowTotal = totals[name] || 1;
        return {
          name,
          values: AGE_BANDS.map(b => {
            const found = rows.find(r => r.name === name && r.ageBand === b);
            return Math.round((numVal(found && found.count) / rowTotal) * 100);
          }),
        };
      });
    },
    ageItems() {
      return AGE_BANDS.map(b => ({
        name: `${b} ปี`,
        value: this.payload.demographics
          .filter(d => d.ageBand === b)
          .reduce((s, d) => s + numVal(d.patients), 0),
      }));
    },
    genderSegments() {
      const total =
        this.payload.demographics.reduce((s, d) => s + numVal(d.patients), 0) || 1;
      const colors = { หญิง: "#2a78d6", ชาย: "#1baf7a", ไม่ระบุ: "#c6d0da" };
      return ["หญิง", "ชาย", "ไม่ระบุ"]
        .map(name => {
          const part = this.payload.demographics
            .filter(d => d.gender === name)
            .reduce((s, d) => s + numVal(d.patients), 0);
          return { name, pct: Math.round((part / total) * 100), color: colors[name] };
        })
        .filter(g => g.pct > 0);
    },
    funnelItems() {
      return this.payload.funnel.map(f => ({
        name: `ครั้งที่ ${f.visitNo}`,
        value: f.visits,
        label: this.funnelLabel(f),
      }));
    },
    newRetChartData() {
      const months = this.payload.newReturning.map(r => r.month);
      return {
        labels: months,
        datasets: [
          {
            label: "รายเดิม",
            backgroundColor: "#2a78d6",
            data: this.payload.newReturning.map(r => numVal(r.returning)),
          },
          {
            label: "รายใหม่ (ใช้สิทธิครั้งแรก)",
            backgroundColor: "#1baf7a",
            data: this.payload.newReturning.map(r => numVal(r.newPatients)),
          },
        ],
      };
    },
    stackedBarOptions() {
      return {
        maintainAspectRatio: false,
        legend: {
          position: "top",
          align: "start",
          labels: { boxWidth: 14, fontFamily: "IBM Plex Sans Thai", fontColor: "#46586a" },
        },
        scales: {
          xAxes: [{ stacked: true, gridLines: { display: false } }],
          yAxes: [
            {
              stacked: true,
              ticks: { beginAtZero: true, callback: v => v.toLocaleString("th-TH") },
              gridLines: { color: "#e3e9ef" },
            },
          ],
        },
        tooltips: { mode: "index", intersect: false },
      };
    },
    blueRamp() {
      return ["#cde2fb", "#9ec5f4", "#6da7ec", "#3987e5", "#256abf", "#184f95", "#0d366b"];
    },
    aquaRamp() {
      return ["#d9f3e8", "#aee4cf", "#7ed3b3", "#4dc096", "#1baf7a", "#128a5f", "#0a6a49"];
    },
    ordinalRamp() {
      return ["#86b6ef", "#5598e7", "#2a78d6", "#1c5cab", "#104281"];
    },
  },
  created() {
    // Sync initial filters from query (supports ?period=&areaType=&mock=1).
    const q = this.$route.query || {};
    if (q.period) this.filters.period = q.period;
    if (q.areaType) this.filters.areaType = q.areaType;
    if (q.area) this.filters.area = q.area;
    if (q.subitem) this.filters.subitem = q.subitem;
    this.load();
  },
  beforeDestroy() {
    clearTimeout(this._loadTimer);
  },
  methods: {
    fmt: fmtVal,
    diagLabel(code) {
      const name = this.payload.diagNames && this.payload.diagNames[code];
      return name ? `${code} · ${name}` : code;
    },
    trendCellRaw(month, subitem) {
      const row = this.payload.trend.find(r => r.month === month && r.subitem === subitem);
      return row ? row.visits : 0;
    },
    trendCell(month, subitem) {
      return fmtVal(this.trendCellRaw(month, subitem));
    },
    funnelLabel(f) {
      const first = numVal(this.payload.funnel[0] && this.payload.funnel[0].visits) || 1;
      const pct = Math.round((numVal(f.visits) / first) * 100);
      return `${fmtVal(f.visits)} (${pct}%)`;
    },
    async load() {
      this.loading = true;
      this.errored = false;
      const mock = this.$route.query && this.$route.query.mock === "1";
      try {
        this.payload = await this.$store.dispatch("nhsoDashboard/fetchDashboard", {
          ...this.filters,
          mock,
        });
      } catch (err) {
        this.errored = true;
      } finally {
        this.loading = false;
      }
    },
    onFilterChange(f) {
      this.filters = f;
      // debounce: bursty changes (e.g. เลือกหลายจังหวัดทีละอัน) รวมเป็นคำขอเดียว
      clearTimeout(this._loadTimer);
      this._loadTimer = setTimeout(() => this.load(), 350);
    },
    onExport() {
      if (this.payload) exportDashboardExcel(this.payload, this.filters);
    },
  },
};
</script>

<style>
@import url("https://fonts.googleapis.com/css2?family=IBM+Plex+Sans+Thai:wght@400;500;600;700&display=swap");
</style>

<style scoped>
.nhso-dash {
  --nd-page: #eef2f6;
  --nd-card: #ffffff;
  --nd-surface2: #f5f8fb;
  --nd-text: #132430;
  --nd-text2: #46586a;
  --nd-muted: #7c8b99;
  --nd-grid: #e3e9ef;
  --nd-baseline: #c6d0da;
  --nd-border: rgba(19, 36, 48, 0.1);
  --nd-accent: #1668b3;
  --nd-good: #0b7a43;
  --nd-good-bg: rgba(11, 122, 67, 0.1);
  --nd-shadow: 0 1px 2px rgba(19, 36, 48, 0.05), 0 1px 3px rgba(19, 36, 48, 0.05);
  --nd-shadow-hover: 0 2px 4px rgba(19, 36, 48, 0.06), 0 8px 20px rgba(19, 36, 48, 0.1);
  font-family: "IBM Plex Sans Thai", "IBM Plex Sans", system-ui, -apple-system, sans-serif;
  background: var(--nd-page);
  color: var(--nd-text);
  min-height: 100vh;
  padding: 30px 20px 56px;
  max-width: 1200px;
  margin: 0 auto;
  font-size: 14px;
  line-height: 1.5;
  -webkit-font-smoothing: antialiased;
}
.nd-header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px 14px;
  margin-bottom: 18px;
}
.nd-logo {
  height: 58px;
  width: auto;
  flex: none;
  object-fit: contain;
}
.nd-credit {
  margin-top: 22px;
  padding: 14px 18px;
  background: var(--nd-card);
  border: 1px solid var(--nd-border);
  border-radius: 12px;
  font-size: 12px;
  color: var(--nd-muted);
  box-shadow: var(--nd-shadow);
  line-height: 1.6;
  text-align: center;
}
.nd-credit b {
  color: var(--nd-text2);
  font-weight: 600;
}
.nd-titles {
  margin-right: auto;
}
.nd-header h1 {
  font-size: 22px;
  font-weight: 700;
  letter-spacing: -0.01em;
  line-height: 1.2;
  margin: 0;
}
.nd-sub {
  color: var(--nd-text2);
  margin: 4px 0 0;
  max-width: 78ch;
  font-size: 13px;
}
.nd-asof {
  color: var(--nd-text2);
  font-size: 12px;
  font-weight: 500;
  background: var(--nd-card);
  border: 1px solid var(--nd-border);
  border-radius: 20px;
  padding: 5px 12px;
  box-shadow: var(--nd-shadow);
  align-self: flex-start;
}
.nd-loading {
  text-align: center;
  padding: 60px 0;
}
.nd-error {
  text-align: center;
  padding: 40px 0;
  color: var(--nd-text2);
}
.nd-kpis {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 12px;
  margin-bottom: 14px;
}
.nd-kpis-2 {
  grid-template-columns: 1fr 1fr;
  margin-bottom: 0;
}
.nd-grid2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  margin-bottom: 14px;
}
.nd-refetching {
  opacity: 0.5;
  pointer-events: none;
  transition: opacity 0.2s;
}
.sec-sub {
  font-size: 12px;
  color: var(--nd-muted);
  margin: 16px 0 8px;
}
.nd-gender {
  display: flex;
  height: 24px;
  border-radius: 8px;
  overflow: hidden;
  gap: 2px;
}
.nd-gender-seg {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
}
.nd-details {
  margin-top: 12px;
}
.nd-details summary {
  font-size: 12px;
  color: var(--nd-muted);
  cursor: pointer;
}
.nd-table {
  border-collapse: collapse;
  width: 100%;
  font-size: 12.5px;
  margin-top: 8px;
}
.nd-table th {
  text-align: left;
  color: var(--nd-text2);
  font-weight: 600;
  border-bottom: 1px solid var(--nd-grid);
  padding: 5px 8px 5px 0;
}
.nd-table td {
  border-bottom: 1px solid var(--nd-grid);
  padding: 5px 8px 5px 0;
  font-variant-numeric: tabular-nums;
}
@media (max-width: 900px) {
  .nd-kpis {
    grid-template-columns: repeat(3, 1fr);
  }
  .nd-grid2 {
    grid-template-columns: 1fr;
  }
}
@media (max-width: 560px) {
  .nd-kpis {
    grid-template-columns: repeat(2, 1fr);
  }
}
</style>
